import { Box, MenuItem, TextField, Typography } from "@mui/material";
import { DropdownIcon } from "app/icons";
import React, { UIEvent } from "react";

import {
  RegistrationFieldConfig,
} from "../../hooks/useAddRegistrationModal";
import { RegistrationSelectOption } from "../../data";
import { useStyles } from "./styles";

type Props = {
  field: RegistrationFieldConfig;
  value: string;
  disabled?: boolean;
  isNumber?: boolean;
  fieldLabel: (label: string) => string;
  placeholder: (
    label: string,
    type: RegistrationFieldConfig["type"],
  ) => string;
  optionLabel: (option: RegistrationSelectOption) => string;
  onChange: (value: string) => void;
  onMenuScroll?: (event: UIEvent<HTMLElement>) => void;
};

const AddRegistrationField = ({
  field,
  value,
  disabled,
  isNumber,
  fieldLabel,
  placeholder,
  optionLabel,
  onChange,
  onMenuScroll,
}: Props) => {
  const styles = useStyles();
  const { key, label, type, options = [], fullWidth } = field;
  const isSelect = type === "select";
  const isTextarea = type === "textarea";

  return (
    <Box
      className={`${styles.field}${fullWidth ? ` ${styles.fullWidth}` : ""}`}
    >
      <Typography className={styles.label}>{fieldLabel(label)}</Typography>
      <TextField
        className={isTextarea ? styles.notesInput : styles.input}
        fullWidth
        select={isSelect}
        multiline={isTextarea}
        variant="outlined"
        placeholder={placeholder(label, type)}
        value={value}
        disabled={disabled}
        InputProps={{ notched: false }}
        {...(isNumber && !isSelect ? { inputMode: "numeric" as const } : {})}
        {...(isSelect
          ? {
              SelectProps: {
                displayEmpty: true,
                IconComponent: DropdownIcon,
                MenuProps: {
                  disableScrollLock: true,
                  PaperProps: {
                    className: styles.selectMenuPaper,
                    onScroll: onMenuScroll,
                  },
                  MenuListProps: {
                    className: styles.selectMenuList,
                    onScroll: onMenuScroll,
                  },
                },
                renderValue: (selected) => {
                  const selectedOption = options.find(
                    (option) => option.value === selected,
                  );
                  return selectedOption ? (
                    optionLabel(selectedOption)
                  ) : (
                    <span className={styles.placeholder}>
                      {placeholder(label, "select")}
                    </span>
                  );
                },
              },
            }
          : {})}
        onChange={(event) => onChange(event.target.value)}
      >
        {isSelect
          ? [
              <MenuItem
                key={`${key}-empty`}
                value=""
                disabled
                className={styles.selectMenuItem}
              >
                {placeholder(label, "select")}
              </MenuItem>,
              ...options.map((option) => (
                <MenuItem
                  key={`${key}-${option.value}`}
                  value={option.value}
                  className={styles.selectMenuItem}
                >
                  {optionLabel(option)}
                </MenuItem>
              )),
            ]
          : null}
      </TextField>
    </Box>
  );
};

export default AddRegistrationField;
